import { AccountabilityChartNode } from 'types/api/Tenant/AccountabilityTypes';

export interface AccountabilityUserFilterOption {
  id: string;
  label: string;
}

/** Derives a unique, alphabetically ordered list of accountable users from the chart. */
export function deriveAccountabilityUserFilterOptions(root?: AccountabilityChartNode | null): AccountabilityUserFilterOption[] {
  if (!root) return [];

  const usersById = new Map<string, AccountabilityUserFilterOption>();

  const visit = (node: AccountabilityChartNode) => {
    const id = node.accountablePerson?.id?.trim();
    const label = node.accountablePerson?.label?.trim();

    if (id) {
      const existing = usersById.get(id);
      if (!existing || (existing.label === 'Unnamed user' && label)) {
        usersById.set(id, { id, label: label || 'Unnamed user' });
      }
    }

    (node.children ?? []).forEach(visit);
  };

  visit(root);

  return [...usersById.values()].sort(
    (first, second) =>
      first.label.localeCompare(second.label, undefined, { sensitivity: 'base' }) || first.id.localeCompare(second.id),
  );
}

/** Keeps every node assigned to the selected user together with its ancestor paths. */
export function filterAccountabilityTreeByUser(
  root: AccountabilityChartNode,
  selectedUserId?: string | null
): AccountabilityChartNode | undefined {
  const userId = selectedUserId?.trim();
  if (!userId) return root;

  const retainMatches = (node: AccountabilityChartNode): AccountabilityChartNode | undefined => {
    const children = (node.children ?? []).map(retainMatches).filter((child): child is AccountabilityChartNode => Boolean(child));
    const isMatch = node.accountablePerson?.id?.trim() === userId;

    if (!isMatch && children.length === 0) return undefined;

    return {
      ...node,
      children,
    };
  };

  return retainMatches(root);
}
